import { useLayoutEffect, useState } from 'react'

/** Attach when the actual sidebar mounts, including after asynchronous CMS loading. */
export function useSmartSticky({
  topOffset = 104,
  bottomOffset = 32,
  minWidth = 1024,
} = {}) {
  const [element, setElement] = useState(null)

  useLayoutEffect(() => {
    if (!element) return

    const properties = ['position', 'top', 'align-self']
    const original = properties.map((name) => [
      name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name),
    ])
    let previousScroll = window.scrollY
    let currentTop = topOffset
    let active = false
    let frame = 0

    const restore = () => {
      original.forEach(([name, value, priority]) => {
        if (value) element.style.setProperty(name, value, priority)
        else element.style.removeProperty(name)
      })
    }

    const update = () => {
      frame = 0
      const scroll = window.scrollY
      const delta = scroll - previousScroll
      previousScroll = scroll

      if (window.innerWidth < minWidth) {
        if (active) restore()
        active = false
        currentTop = topOffset
        return
      }

      if (!active) {
        element.style.setProperty('position', 'sticky')
        element.style.setProperty('align-self', 'start')
        currentTop = topOffset
        active = true
      }

      const height = element.getBoundingClientRect().height
      const bottomTop = window.innerHeight - bottomOffset - height
      // A tall sidebar travels with the document between these two limits.
      // Changing direction moves the inset immediately, without snapping.
      currentTop = bottomTop >= topOffset
        ? topOffset
        : Math.max(bottomTop, Math.min(topOffset, currentTop - delta))
      element.style.setProperty('top', `${currentTop}px`)
    }

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    const observer = new ResizeObserver(schedule)
    observer.observe(element)

    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      window.cancelAnimationFrame(frame)
      observer.disconnect()
      restore()
    }
  }, [element, topOffset, bottomOffset, minWidth])

  return setElement
}
